import React, { useState, useEffect } from 'react';
import { APP_CONFIG } from '../config';
import { parseError } from '../utils/errorParser';
import { sendTelegramNotification } from '../utils/telegramNotify';
import './Registration.css';

function Registration({ account, tokenBalance, registerAndInvest, addNotification, connectWallet, isConnecting }) {
  const [referrerId, setReferrerId] = useState('');
  const [investAmount, setInvestAmount] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState(1);

  useEffect(() => {
    const savedRef = localStorage.getItem('alphameta_referrer');
    const urlParams = new URLSearchParams(window.location.search);
    const refId = urlParams.get('ref') || savedRef;
    if (refId) setReferrerId(refId);
  }, []);

  const handleRegister = async () => {
    const amount = parseFloat(investAmount);
    if (!amount || amount < APP_CONFIG.minInvestment) {
      addNotification('error', `Minimum investment is $${APP_CONFIG.minInvestment}`);
      return;
    }
    if (amount % APP_CONFIG.investmentMultiple !== 0) {
      addNotification('error', `Amount must be multiple of $${APP_CONFIG.investmentMultiple}`);
      return;
    }
    
    // Check balance
    const balance = parseFloat(tokenBalance);
    if (balance < amount) {
      addNotification('error', `Insufficient balance. You have $${balance.toFixed(2)} USDT`);
      return;
    }

    setIsLoading(true);
    try {
      const refId = referrerId ? parseInt(referrerId) : APP_CONFIG.defaultReferrerId;
      await registerAndInvest(refId, investAmount);
      addNotification('success', 'Registration successful! Welcome to AlphaMeta!');
      
      // Note: Telegram notification will be sent after user links Telegram
      // Can't send now because user doesn't have a userId yet until page refreshes
      
    } catch (error) {
      const friendlyMessage = parseError(error);
      addNotification('error', friendlyMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="registration-page">
      <div className="reg-container">
        {/* Logo and Title */}
        <div className="reg-header">
          <img src="/logo.png" alt="AlphaMeta" className="reg-logo" />
          <h1 className="reg-title">Welcome to <span className="gradient-text">AlphaMeta</span></h1>
          <p className="reg-subtitle">AI-Powered DeFi Trading Platform</p>
        </div>

        {/* Hero GIF */}
        <div className="reg-hero">
          <img src="/1.gif" alt="AI Trading" className="reg-gif" />
        </div>

        {/* Features */}
        <div className="reg-features">
          <div className="feature-item">
            <span className="feature-icon">🤖</span>
            <span className="feature-text">AI Trading Bots</span>
          </div>
          <div className="feature-item">
            <span className="feature-icon">📈</span>
            <span className="feature-text">0.5% - 1% Daily Profit</span>
          </div>
          <div className="feature-item">
            <span className="feature-icon">💎</span>
            <span className="feature-text">Up to 300% Returns</span>
          </div>
        </div>

        {/* Steps */}
        <div className="reg-steps">
          <div className={`step-indicator ${step >= 1 ? 'active' : ''}`}>1</div>
          <div className={`step-line ${step >= 2 ? 'active' : ''}`} />
          <div className={`step-indicator ${step >= 2 ? 'active' : ''}`}>2</div>
          <div className={`step-line ${step >= 3 ? 'active' : ''}`} />
          <div className={`step-indicator ${step >= 3 ? 'active' : ''}`}>3</div>
        </div>

        {/* Step Content */}
        {!account ? (
          <div className="reg-step-content">
            <h2>Step 1: Connect Your Wallet</h2>
            <p>Connect your wallet to get started with AlphaMeta</p>
            <button className="reg-btn primary" onClick={connectWallet} disabled={isConnecting}>
              {isConnecting ? <><span className="btn-spinner" />Connecting...</> : (
                <>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 12V7H5a2 2 0 0 1 0-4h14v4" />
                    <path d="M3 5v14a2 2 0 0 0 2 2h16v-5" />
                    <path d="M18 12a2 2 0 0 0 0 4h4v-4h-4z" />
                  </svg>
                  Connect Wallet
                </>
              )}
            </button>
          </div>
        ) : step === 1 ? (
          <div className="reg-step-content animate-fade-in">
            <h2>Step 2: Enter Referral ID</h2>
            <p>Enter your sponsor's referral ID (optional)</p>
            <div className="input-group">
              <input
                type="number"
                placeholder={`Default: ${APP_CONFIG.defaultReferrerId}`}
                value={referrerId}
                onChange={(e) => setReferrerId(e.target.value)}
              />
            </div>
            <button className="reg-btn primary" onClick={() => setStep(2)}>Continue</button>
            <button className="reg-btn secondary" onClick={() => setStep(2)}>Skip</button>
          </div>
        ) : (
          <div className="reg-step-content animate-fade-in">
            <h2>Step 3: Initial Investment</h2>
            <p>Make your first investment to activate your account</p>
            
            <div className="investment-info">
              <div className="info-row">
                <span>Daily Profit</span>
                <span className="info-value green">0.5% - 1%</span>
              </div>
              <div className="info-row">
                <span>Max Return</span>
                <span className="info-value cyan">300%</span>
              </div>
              <div className="info-row">
                <span>Minimum</span>
                <span className="info-value">${APP_CONFIG.minInvestment} USDT</span>
              </div>
            </div>

            <div className="input-group">
              <label>Investment Amount (USDT)</label>
              <input
                type="number"
                placeholder={`Min: $${APP_CONFIG.minInvestment}`}
                value={investAmount}
                onChange={(e) => setInvestAmount(e.target.value)}
                step={APP_CONFIG.investmentMultiple}
                min={APP_CONFIG.minInvestment}
              />
            </div>

            <div className="quick-amounts">
              {[10, 50, 100, 500, 1000].map(amt => (
                <button key={amt} className="quick-btn" onClick={() => setInvestAmount(amt.toString())}>${amt}</button>
              ))}
            </div>

            <div className="wallet-balance">
              Balance: ${parseFloat(tokenBalance).toFixed(2)} USDT
            </div>

            <button className="reg-btn primary" onClick={handleRegister} disabled={isLoading}>
              {isLoading ? <><span className="btn-spinner" />Processing...</> : 'Register & Invest'}
            </button>
            <button className="reg-btn secondary" onClick={() => setStep(1)}>Back</button>
          </div>
        )}

        {/* Security Note */}
        <div className="security-note">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
          <span>Secured by Smart Contract on BNB Chain</span>
        </div>
      </div>
    </div>
  );
}

export default Registration;
